import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms'

import { TreeNode } from 'primeng/api'
import { TooltipModule } from 'primeng/tooltip'
import { TreeSelectModule } from 'primeng/treeselect'

import {
  FlxButtonComponent,
  FlxErrorComponent,
  FlxSelectComponent,
  SelectItem,
} from '@flowx/angular-ui-toolkit'
import {
  UserWorkspaceAssignments,
  UserWorkspaceAssignmentsFormGroup,
} from '@flowx/designer/modules/core/dictionary/access-management.dictionary'
import { TOOLTIP_DEFAULT_DELAY } from '@flowx/designer/modules/core/dictionary/constants'
import {
  WorkspaceDefinition,
  WorkspaceType,
} from '@flowx/designer/modules/workspaces/shared/commons.types'

import { WorkspaceEnvTypeTagComponent } from '../../workspace-env-type-tag/workspace-env-type-tag.component'

@Component({
  selector: 'flx-workspace-assignments',
  templateUrl: './workspace-assignments.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TreeSelectModule,
    TooltipModule,
    FlxButtonComponent,
    FlxErrorComponent,
    FlxSelectComponent,
    WorkspaceEnvTypeTagComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WorkspaceAssignmentsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WorkspaceAssignmentsComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    ::ng-deep .flx-prime-tree-select .p-virtualscroller-content {
      width: 100%;
    }
  `,
})
export class WorkspaceAssignmentsComponent implements ControlValueAccessor, Validator {
  #formBuilder = inject(FormBuilder)

  protected readonly TOOLTIP_DEFAULT_DELAY = TOOLTIP_DEFAULT_DELAY

  availableWorkspacesForUser = input.required<WorkspaceDefinition[]>()
  userRoleOptions = input.required<SelectItem<string>[]>()
  enableErrorValidation = input.required()

  rolesFilter = signal('')
  filteredUserRoleOptions = computed(() => {
    const filter = this.rolesFilter().toLowerCase()
    const options = this.userRoleOptions()

    return filter
      ? options.filter((option) => option.label.toLowerCase().includes(filter))
      : options
  })

  workspaceAssignmentsFA: FormArray<FormGroup<UserWorkspaceAssignmentsFormGroup>> =
    this.#formBuilder.array<FormGroup<UserWorkspaceAssignmentsFormGroup>>([])
  selectedWorkspacesEnvTypes = signal<{ [workspaceId: string]: WorkspaceType }>({})
  isDisabled = signal(false)

  workspaceAssignmentsValue = toSignal(this.workspaceAssignmentsFA.valueChanges, {
    initialValue: this.workspaceAssignmentsFA.getRawValue(),
  })

  availableWorkspacesById = computed(() => {
    const workspaces = this.availableWorkspacesForUser()

    return workspaces.reduce(
      (map, workspace) => {
        map[workspace.id] = workspace
        return map
      },
      {} as { [workspaceId: string]: WorkspaceDefinition }
    )
  })

  workspaceNodesById = computed(() =>
    this.groupedWorkspaces()
      .flatMap((group) => group.children ?? [])
      .reduce(
        (map, node) => {
          map[node.key] = node
          return map
        },
        {} as { [workspaceId: string]: TreeNode }
      )
  )

  groupedWorkspaces = computed(() => {
    const availableWorkspacesForUser = this.availableWorkspacesForUser()
    const value = this.workspaceAssignmentsValue()

    if (!availableWorkspacesForUser.length) {
      return []
    }

    return availableWorkspacesForUser.reduce(
      (groups: TreeNode[], workspace: WorkspaceDefinition): TreeNode[] => {
        let group = groups.find((item) => item.key === workspace.envType)

        if (!group) {
          group = {
            key: workspace.envType,
            label: workspace.envType,
            children: [],
            expanded: true,
            selectable: false,
            leaf: false,
            type: 'group',
          } satisfies TreeNode

          groups.push(group)
        }

        group.children.push({
          key: workspace.id,
          label: workspace.name,
          data: workspace,
          selectable: value.every((assignment) => assignment.workspaceId !== workspace.id),
          leaf: true,
          type: 'item',
        } satisfies TreeNode<WorkspaceDefinition>)

        return groups
      },
      []
    )
  })

  constructor() {
    effect(() => {
      const workspaceAssignmentsValue = this.workspaceAssignmentsValue()
      this.#onChange(
        workspaceAssignmentsValue.map((assignment) => ({
          ...assignment,
          roleIds: (assignment.roleIds ?? []).map((role) => role.value),
        })) as UserWorkspaceAssignments[]
      )
      this.#onValidatorChange()
    })
  }

  // ControlValueAccessor
  writeValue(value: UserWorkspaceAssignments[]): void {
    this.workspaceAssignmentsFA.clear({ emitEvent: false })

    const assignments = value?.length ? value : [null]
    assignments.forEach((assignment) =>
      this.workspaceAssignmentsFA.push(this.#newWorkspaceRoleFormGroup(assignment), {
        emitEvent: true,
      })
    )

    // Pushing enabled groups re-enables the array, so re-apply the disabled state — Angular does
    // not re-call setDisabledState on a plain value write.
    if (this.isDisabled()) {
      this.workspaceAssignmentsFA.disable({ emitEvent: false })
    }

    this.selectedWorkspacesEnvTypes.set(this.#resolveEnvTypes(value ?? []))
  }

  registerOnChange(fn: (value: UserWorkspaceAssignments[]) => void): void {
    this.#onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled)
    if (isDisabled) {
      this.workspaceAssignmentsFA.disable({ emitEvent: false })
    } else {
      this.workspaceAssignmentsFA.enable({ emitEvent: false })
      this.#onValidatorChange()
    }
  }

  // Validator
  validate(): ValidationErrors | null {
    return this.workspaceAssignmentsFA.valid ? null : { workspaceAssignmentsInvalid: true }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.#onValidatorChange = fn
  }

  addWorkspaceAssignment(): void {
    if (this.isDisabled()) {
      return
    }

    this.workspaceAssignmentsFA.push(this.#newWorkspaceRoleFormGroup())
  }

  removeWorkspaceAssignments(index: number): void {
    this.workspaceAssignmentsFA.removeAt(index)
  }

  onWorkspaceSelect(selectedWorkspace: WorkspaceDefinition, index: number): void {
    const workspaceAssignmentFG = this.workspaceAssignmentsFA.at(index)

    if (workspaceAssignmentFG) {
      workspaceAssignmentFG.patchValue({ workspaceId: selectedWorkspace.id })
      this.selectedWorkspacesEnvTypes.set({
        ...this.selectedWorkspacesEnvTypes(),
        [selectedWorkspace.id]: selectedWorkspace.envType,
      })
    }

    this.#onTouched()
  }

  #resolveEnvTypes(assignments: UserWorkspaceAssignments[]): {
    [workspaceId: string]: WorkspaceType
  } {
    const workspacesById = new Map(
      this.availableWorkspacesForUser().map((workspace) => [workspace.id, workspace])
    )

    return assignments.reduce(
      (envTypes, assignment) => {
        const workspace = workspacesById.get(assignment.workspaceId)
        return workspace ? { ...envTypes, [workspace.id]: workspace.envType } : envTypes
      },
      {} as { [workspaceId: string]: WorkspaceType }
    )
  }

  #newWorkspaceRoleFormGroup(
    assignment?: UserWorkspaceAssignments | null
  ): FormGroup<UserWorkspaceAssignmentsFormGroup> {
    return this.#formBuilder.nonNullable.group({
      workspaceId: [assignment?.workspaceId ?? null, Validators.required],
      roleIds: [this.#toRoleItems(assignment?.roleIds ?? []), Validators.required],
    })
  }

  #toRoleItems(roleIds: string[]): SelectItem<string>[] {
    const optionsByValue = new Map(this.userRoleOptions().map((option) => [option.value, option]))
    return roleIds.map((id) => optionsByValue.get(id) ?? { value: id, label: id, enabled: true })
  }

  #onChange: (value: UserWorkspaceAssignments[]) => void = () => {}
  #onTouched: () => void = () => {}
  #onValidatorChange: () => void = () => {}
}
